import { useState, useEffect, useCallback } from 'react';
import { Shield, Settings, BarChart3, Users, MapPin, Search, Star, Globe, ArrowLeft, Save, RefreshCw, Database, TrendingUp, Languages } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { LlmChainEditor } from '../components/admin/LlmChainEditor';

interface AppConfig {
  key: string;
  value: unknown;
  label: string | null;
  updated_at: string;
}

interface SearchRegion {
  region: string;
  searches: number;
}

interface AdminStats {
  total_users: number;
  subscription_breakdown: Record<string, number>;
  total_itineraries: number;
  total_saved_places: number;
  total_reviews: number;
  avg_review_rating: number | null;
  active_countries: string[] | null;
  top_search_regions: SearchRegion[] | null;
  cache_total_entries: number;
  cache_total_hits: number;
  cache_size_mb: number;
  searches_this_month: number;
  users_this_month: number;
  language_breakdown: Record<string, number>;
}

interface GrowthPoint {
  label: string;
  users: number;
  searches: number;
}

/** Simple SVG bar chart for growth data */
function GrowthChart({ data, dataKey, color }: { data: GrowthPoint[]; dataKey: 'users' | 'searches'; color: string }) {
  if (data.length === 0) return null;
  const values = data.map(d => d[dataKey]);
  const max = Math.max(...values, 1);

  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((d, i) => {
        const h = Math.max(2, (d[dataKey] / max) * 100);
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className="text-[8px] text-[var(--text-secondary)] font-semibold">{d[dataKey] || ''}</span>
            <div
              className="w-full rounded-t-sm transition-all"
              style={{ height: `${h}%`, backgroundColor: color, minHeight: '2px' }}
            />
            <span className="text-[7px] text-[var(--text-secondary)] truncate w-full text-center">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminPage() {
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<AppConfig[]>([]);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [growth, setGrowth] = useState<GrowthPoint[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmSave, setConfirmSave] = useState<{ key: string; oldValue: string; newValue: string } | null>(null);
  const [auditLog, setAuditLog] = useState<{ config_key: string; old_value: unknown; new_value: unknown; changed_by_email: string; created_at: string }[]>([]);

  useEffect(() => {
    if (!adminLoading) setLoading(false);
  }, [adminLoading]);

  // Keys that require a confirmation modal before saving
  const SENSITIVE_PREFIXES = ['BETA_MODE', 'FREE_', 'PRICE_', 'MAX_FREE_'];

  const isSensitiveKey = (key: string) =>
    SENSITIVE_PREFIXES.some(p => key.startsWith(p));

  const loadAudit = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.rpc('get_config_audit', { p_limit: 10 });
    if (!error && Array.isArray(data)) setAuditLog(data);
  }, []);

  const loadConfig = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase.from('app_config').select('*').order('key');
    if (data) {
      setConfig(data);
      const vals: Record<string, string> = {};
      data.forEach(c => { vals[c.key] = JSON.stringify(c.value); });
      setEditedValues(vals);
    }
  }, []);

  const loadStats = useCallback(async () => {
    if (!supabase) return;
    setStatsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_admin_stats');
      if (error) { console.error('Stats error:', error); return; }
      setStats(data as AdminStats);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadGrowth = useCallback(async () => {
    if (!supabase) return;
    // Get weekly growth for the last 12 weeks
    const weeks: GrowthPoint[] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - (i * 7));
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);

      const label = weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      weeks.push({ label, users: 0, searches: 0 });
    }

    // Fetch user_settings created_at for user growth
    const twelveWeeksAgo = new Date(now);
    twelveWeeksAgo.setDate(twelveWeeksAgo.getDate() - 84);

    const [usersRes, searchesRes] = await Promise.all([
      supabase
        .from('user_settings')
        .select('created_at')
        .gte('created_at', twelveWeeksAgo.toISOString())
        .order('created_at'),
      supabase
        .from('itineraries')
        .select('created_at')
        .gte('created_at', twelveWeeksAgo.toISOString())
        .order('created_at'),
    ]);

    const binToWeek = (dateStr: string) => {
      const d = new Date(dateStr);
      const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
      const weekIndex = 11 - Math.floor(diffDays / 7);
      return Math.max(0, Math.min(11, weekIndex));
    };

    if (usersRes.data) {
      for (const row of usersRes.data) {
        weeks[binToWeek(row.created_at)].users++;
      }
    }
    if (searchesRes.data) {
      for (const row of searchesRes.data) {
        weeks[binToWeek(row.created_at)].searches++;
      }
    }

    setGrowth(weeks);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadConfig();
      loadStats();
      loadGrowth();
      loadAudit();
    }
  }, [isAdmin, loadConfig, loadStats, loadGrowth, loadAudit]);

  const trySaveConfig = (key: string) => {
    const raw = editedValues[key];
    const currentConfig = config.find(c => c.key === key);
    const oldValue = currentConfig ? JSON.stringify(currentConfig.value) : '';
    if (isSensitiveKey(key)) {
      setConfirmSave({ key, oldValue, newValue: raw });
    } else {
      doSaveConfig(key);
    }
  };

  const doSaveConfig = async (key: string) => {
    if (!supabase) return;
    setConfigSaving(key);
    setConfirmSave(null);
    try {
      const raw = editedValues[key];
      let parsed: unknown;
      try { parsed = JSON.parse(raw); } catch { parsed = raw; }
      const { error } = await supabase.rpc('update_admin_config', { p_key: key, p_value: parsed });
      if (error) { showToast(`Error: ${error.message}`); return; }
      showToast(`${key} saved`);
      loadConfig();
      loadAudit();
    } finally {
      setConfigSaving(null);
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--border)] border-t-[var(--accent-teal)]" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center gap-4 px-4">
        <Shield className="w-16 h-16 text-[var(--text-secondary)]" />
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Access Denied</h1>
        <p className="text-sm text-[var(--text-secondary)]">This page is restricted to admin users.</p>
        <a href="/" className="mt-4 px-4 py-2 rounded-lg bg-[var(--accent-teal)] text-white text-sm font-semibold">
          Back to App
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] pb-12">
      {/* Toast */}
      {toast && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-lg bg-[var(--accent-teal)] text-white text-sm font-semibold shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="bg-[var(--bg-elevated)] border-b border-[var(--border)] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <a href="/" className="p-1 rounded-lg hover:bg-[var(--bg-primary)] transition-colors">
            <ArrowLeft className="w-5 h-5 text-[var(--text-secondary)]" />
          </a>
          <Shield className="w-5 h-5 text-[var(--accent-gold)]" />
          <h1 className="text-lg font-bold text-[var(--text-primary)]">Admin</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">

        {/* ============ ANALYTICS ============ */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[var(--accent-teal)]" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">Analytics</h2>
            </div>
            <button
              onClick={() => { loadStats(); loadGrowth(); }}
              disabled={statsLoading}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-elevated)] transition-colors"
            >
              <RefreshCw className={`w-4 h-4 text-[var(--text-secondary)] ${statsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {statsLoading && !stats ? (
            <div className="flex justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--accent-teal)]" />
            </div>
          ) : stats ? (
            <div className="space-y-3">
              {/* Key metrics grid */}
              <div className="grid grid-cols-2 gap-3">
                <StatCard icon={<Users className="w-4 h-4" />} label="Total Users" value={stats.total_users} />
                <StatCard icon={<Users className="w-4 h-4" />} label="New This Month" value={stats.users_this_month} />
                <StatCard icon={<Search className="w-4 h-4" />} label="Total Trips" value={stats.total_itineraries} />
                <StatCard icon={<Search className="w-4 h-4" />} label="Searches This Month" value={stats.searches_this_month} />
                <StatCard icon={<MapPin className="w-4 h-4" />} label="Saved Places" value={stats.total_saved_places} />
                <StatCard icon={<Star className="w-4 h-4" />} label="Reviews" value={`${stats.total_reviews}${stats.avg_review_rating ? ` (${stats.avg_review_rating}★)` : ''}`} />
              </div>

              {/* Growth charts */}
              {growth.length > 0 && (
                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-3.5 h-3.5 text-[var(--accent-teal)]" />
                      <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">New Users — 12 weeks</h3>
                    </div>
                    <GrowthChart data={growth} dataKey="users" color="var(--accent-teal)" />
                  </div>
                  <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-3.5 h-3.5 text-[var(--accent-gold)]" />
                      <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Searches — 12 weeks</h3>
                    </div>
                    <GrowthChart data={growth} dataKey="searches" color="var(--accent-gold)" />
                  </div>
                </div>
              )}

              {/* Subscription breakdown */}
              <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-2">Subscriptions</h3>
                <div className="space-y-1.5">
                  {Object.entries(stats.subscription_breakdown || {}).sort(([a], [b]) => a.localeCompare(b)).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between text-sm">
                      <span className="text-[var(--text-secondary)] capitalize">{type.replace('_', ' ')}</span>
                      <span className="font-semibold text-[var(--text-primary)]">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Language popularity */}
              {stats.language_breakdown && Object.keys(stats.language_breakdown).length > 0 && (
                <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                  <div className="flex items-center gap-2 mb-2">
                    <Languages className="w-3.5 h-3.5 text-[var(--accent-teal)]" />
                    <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Languages</h3>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(stats.language_breakdown)
                      .sort(([, a], [, b]) => b - a)
                      .map(([lang, count]) => {
                        const pct = stats.total_users > 0 ? Math.round((count / stats.total_users) * 100) : 0;
                        const labels: Record<string, string> = { en: 'English', fr: 'Français', es: 'Español', de: 'Deutsch' };
                        return (
                          <div key={lang} className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-sm mb-0.5">
                                <span className="text-[var(--text-secondary)]">{labels[lang] || lang.toUpperCase()}</span>
                                <span className="font-semibold text-[var(--text-primary)]">{count} <span className="text-[10px] font-normal text-[var(--text-secondary)]">({pct}%)</span></span>
                              </div>
                              <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-[var(--accent-teal)] transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Top search regions */}
              {stats.top_search_regions && stats.top_search_regions.length > 0 && (
                <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-3.5 h-3.5 text-[var(--accent-gold)]" />
                    <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">
                      Top Search Regions
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {stats.top_search_regions.map((r, i) => (
                      <div key={r.region} className="flex items-center justify-between text-xs">
                        <span className="text-[var(--text-secondary)] truncate flex-1">
                          <span className="text-[var(--text-primary)] font-medium mr-1.5">{i + 1}.</span>
                          {r.region}
                        </span>
                        <span className="font-semibold text-[var(--text-primary)] ml-2">{r.searches}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active countries */}
              {stats.active_countries && stats.active_countries.length > 0 && (
                <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-3.5 h-3.5 text-[var(--accent-teal)]" />
                    <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">
                      Active Countries ({stats.active_countries.length})
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {stats.active_countries.map(c => (
                      <span key={c} className="px-2 py-0.5 rounded-full bg-[var(--accent-teal)]/10 text-xs text-[var(--accent-teal)] font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* PoI Cache */}
              <div className="p-4 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-3.5 h-3.5 text-[var(--accent-gold)]" />
                  <h3 className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">PoI Cache</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-lg font-bold text-[var(--text-primary)]">{stats.cache_total_entries}</p>
                    <p className="text-[10px] text-[var(--text-secondary)]">Cached Entries</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[var(--text-primary)]">{stats.cache_total_hits}</p>
                    <p className="text-[10px] text-[var(--text-secondary)]">Cache Hits</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[var(--text-primary)]">{stats.cache_size_mb} MB</p>
                    <p className="text-[10px] text-[var(--text-secondary)]">Data Size</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)]">Failed to load stats.</p>
          )}
        </section>

        {/* ============ APP CONFIG ============ */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Settings className="w-4 h-4 text-[var(--accent-gold)]" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">App Config</h2>
          </div>

          <div className="space-y-2">
            <LlmChainEditor onSaved={msg => { showToast(msg); loadAudit(); }} />
            {config.filter(c => c.key !== 'LLM_CHAIN').map(c => {
              const changed = editedValues[c.key] !== JSON.stringify(c.value);
              return (
                <div key={c.key} className="p-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[var(--text-primary)] truncate">{c.label || c.key}</p>
                      <p className="text-[10px] text-[var(--text-secondary)] font-mono truncate">{c.key}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editedValues[c.key] || ''}
                        onChange={e => setEditedValues(prev => ({ ...prev, [c.key]: e.target.value }))}
                        className="w-20 px-2 py-1 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-sm text-[var(--text-primary)] text-right font-semibold focus:outline-none focus:border-[var(--accent-teal)]"
                      />
                      <button
                        onClick={() => trySaveConfig(c.key)}
                        disabled={!changed || configSaving === c.key}
                        className={`p-1.5 rounded-lg transition-colors ${
                          changed
                            ? 'bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/80'
                            : 'bg-[var(--bg-primary)] text-[var(--text-secondary)]'
                        } disabled:opacity-30`}
                      >
                        <Save className={`w-3.5 h-3.5 ${configSaving === c.key ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-[10px] text-[var(--text-secondary)] mt-2 italic">
            Config changes take effect on next app load. Sensitive keys require confirmation.
          </p>
        </section>

        {/* ============ CONFIG AUDIT LOG ============ */}
        {auditLog.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-[var(--accent-gold)]" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">Recent Config Changes</h2>
            </div>
            <div className="space-y-1.5">
              {auditLog.map((entry, i) => (
                <div key={i} className="p-2.5 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-[var(--text-primary)] truncate">{entry.config_key}</p>
                    <p className="text-[10px] text-[var(--text-secondary)] whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] font-mono text-[var(--status-red)] line-through">{JSON.stringify(entry.old_value)}</span>
                    <span className="text-[10px] text-[var(--text-secondary)]">→</span>
                    <span className="text-[10px] font-mono text-[var(--accent-teal)]">{JSON.stringify(entry.new_value)}</span>
                  </div>
                  <p className="text-[9px] text-[var(--text-secondary)] mt-0.5">{entry.changed_by_email}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Quick links */}
        <section className="pt-2">
          <a
            href="https://www.tripboom.app/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-center text-[var(--accent-teal)] hover:text-[var(--accent-teal)]/80 transition-colors"
          >
            Manage Reviews on Website ↗
          </a>
        </section>
      </div>

      {/* Confirmation modal for sensitive config keys */}
      {confirmSave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmSave(null)} />
          <div className="relative bg-[var(--bg-elevated)] rounded-2xl border border-[var(--border)] p-5 mx-4 max-w-sm w-full shadow-xl">
            <h3 className="text-sm font-bold text-[var(--text-primary)] mb-3">Confirm Config Change</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-1">
              <span className="font-mono font-semibold text-[var(--text-primary)]">{confirmSave.key}</span> is a sensitive setting.
            </p>
            <div className="rounded-lg bg-[var(--bg-primary)] border border-[var(--border)] p-3 my-3 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-secondary)] w-8">Old:</span>
                <span className="text-xs font-mono text-[var(--status-red)]">{confirmSave.oldValue}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-secondary)] w-8">New:</span>
                <span className="text-xs font-mono text-[var(--accent-teal)]">{confirmSave.newValue}</span>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setConfirmSave(null)}
                className="flex-1 px-3 py-2 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => doSaveConfig(confirmSave.key)}
                className="flex-1 px-3 py-2 rounded-xl text-sm font-semibold bg-[var(--accent-gold)] text-black hover:opacity-90 transition-colors"
              >
                Confirm Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <div className="p-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)]">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[var(--accent-teal)]">{icon}</span>
        <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-xl font-bold text-[var(--text-primary)]">{value}</p>
    </div>
  );
}
